import i18n from 'helpers/i18n';
import messages from './messages';
import { useCallback, useContext, useEffect, useId, useState } from 'react';
import useCurrentUser from 'hooks/useCurrentUser';
import RoutesHelper from 'helpers/routes';
import { useLocation } from 'react-router-dom';
import { Video } from '../types';
import { LearningResourcesContext } from 'pages/LearningResources/LearningResourcesContext';
import { toastError } from 'components/Toast';
import { ResourceType } from 'types/api/LearningResource';
import { Progress } from '@teachstonellc/component-library';

interface PlaylistItemProps {
  handleClick: (id: number, type: ResourceType) => void;
  percentage: number;
  numberOfResources: number;
  video: Video;
}

function PlaylistItem({
  percentage,
  numberOfResources,
  handleClick,
  video
}: PlaylistItemProps) {
  const { roles = [] } = useCurrentUser();
  const checkboxId = useId();
  const location = useLocation();
  const currentPath = location.pathname;
  const learningResourcesContext = useContext(LearningResourcesContext);

  const { recommendedVideosArray, setRecommendedVideosArray } =
    learningResourcesContext;

  const [isChecked, setIsChecked] = useState(
    recommendedVideosArray.some(item => item.id === video.id)
  );

  if (!learningResourcesContext) {
    throw new Error(
      'Learning Resources must be used within a VideoTagsProvider'
    );
  }

  const isCheckedItem = useCallback(() => {
    return recommendedVideosArray.some(item => item.id === video.id);
  }, [recommendedVideosArray, video.id]);

  useEffect(() => {
    setIsChecked(isCheckedItem());
  }, [isCheckedItem, recommendedVideosArray]);

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const shouldRecommend = event.target.checked;
    const itemFound = recommendedVideosArray.find(item => item.id === video.id);

    if (shouldRecommend) {
      if (itemFound) {
        toastError({
          description: i18n.ft(messages.error)
        });
        return;
      }

      setRecommendedVideosArray([
        ...recommendedVideosArray,
        {
          name: video.name || '',
          id: video.id || 0,
          videoURI: video.videoURI || '',
          estimatedTime: video.estimatedTime || '',
          type: video.type || 'playlist'
        }
      ]);

      setIsChecked(true);
    } else {
      setRecommendedVideosArray(
        recommendedVideosArray.filter(item => item.id !== video.id)
      );

      setIsChecked(false);
    }
  }

  function renderCoachSection() {
    return (
      <label
        className="mt-auto grid grid-cols-2 items-center rounded-b-lg cursor-pointer hover:bg-ds-surface-hover"
        htmlFor={checkboxId}
      >
        <span className="font-bold pl-3">
          {`${percentage}% ${i18n.ft(messages.complete)}`}
        </span>

        <div className="relative px-3 py-2 flex items-center justify-end gap-2">
          <span>{i18n.ft(messages.recommend)}</span>

          <input
            type="checkbox"
            id={checkboxId}
            checked={isChecked}
            onChange={handleCheckboxChange}
            className="sr-only peer"
          />

          <div className="text-xl text-action w-6 h-6 flex items-center justify-center rounded peer-focus-visible:ring-2 peer-focus-visible:ring-action">
            {isChecked ? (
              <i className="fa-solid fa-square-check" aria-hidden="true" />
            ) : (
              <i className="fa-regular fa-square" aria-hidden="true" />
            )}
          </div>
        </div>
      </label>
    );
  }

  function isCoach() {
    return (
      roles.includes('lr_coach') &&
      currentPath.includes(RoutesHelper.getPath('coach-learning-resources'))
    );
  }

  return (
    <div
      className={`min-w-[286px] flex flex-col mt-4 relative isolate rounded-lg shadow-lg ${isChecked ? 'bg-blue-50 ring-2 ring-blue-600' : 'bg-white'}`}
    >
      <div
        className="relative z-10 min-w-[296px] bg-white rounded-lg h-fit cursor-pointer"
        onClick={() => handleClick(video.id, video.type || 'learning_resource')}
      >
        <div className="snap-start flex flex-shrink-0 relative" tabIndex={0}>
          <img
            className="w-full max-w-full rounded-t-lg object-cover md:!h-[249px] lg:!h-56 2xl:!h-40"
            src={
              video.introImage ||
              `https://cdnsecakmi.kaltura.com/p/966871/thumbnail/entry_id/${video.videoURI}/width/600`
            }
            alt="Thumbnail"
          />
          <div className="mt-2 ml-2 absolute top-0 left-0 bg-white text-black px-2 py-1 rounded text-xs font-semibold">
            {`${numberOfResources} ${i18n.ft(messages.resources)}`}
          </div>
        </div>
      </div>

      <div className="m-3 mt-4 font-semibold text-black text-lg line-clamp-2 leading-6">
        {video.name}
      </div>

      <div className="px-3 pb-3">
        <span className="bg-charcoal-400 text-white inline-block px-2 py-1 font-semibold text-sm leading-4 rounded inline-flex items-center gap-1">
          <i className="fa-regular fa-server fa-sm" />
          <span>{i18n.ft(messages.learningSeries)}</span>
        </span>
      </div>

      <div className="px-3 py-2">
        <Progress value={percentage} height={8} color="bg-blue-600" />
      </div>

      {isCoach() ? (
        renderCoachSection()
      ) : (
        <div className="px-3 pb-3 rounded-b-lg">
          <span className="font-bold">
            {`${percentage}% ${i18n.ft(messages.complete)}`}
          </span>
        </div>
      )}

      <div className="absolute inset-x-0 top-0 flex justify-center">
        <div
          className={`w-10/12 h-6 rounded-t-lg shadow-lg -mt-4 ${isChecked ? 'bg-blue-50 ring-2 ring-blue-600' : ' bg-white ring-2 ring-ds-border'}`}
        ></div>
      </div>

      <div className="absolute inset-x-0 top-0 flex justify-center">
        <div
          className={`w-11/12 h-6 rounded-t-lg shadow-lg -mt-2 ${isChecked ? 'bg-blue-50 ring-2 ring-blue-600' : 'bg-white ring-2 ring-ds-border'}`}
        ></div>
      </div>
    </div>
  );
}

export default PlaylistItem;
